import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ProjectPreview, { type ProjectPreviewRef } from '@/components/ProjectPreview';
import { buildSubtitlePreviewConfig } from '@/lib/preview/buildSubtitlePreviewConfig';
import {
  BRAND_KIT_PREVIEW_VISIBLE_RATIO,
  shouldPlayVisibleBrandKitPreview,
} from '@/lib/brand-kit/shouldPlayVisibleBrandKitPreview';
import { buildBrandKitCardPreviewProps } from '@/lib/brand-kit/buildBrandKitCardPreviewProps';
import { useMyFontsQuery } from '@/generated/graphql';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface BrandKitCardPreviewProps {
  kitData: unknown;
  previewVideoUrl?: string | null;
  previewPosterUrl?: string | null;
  className?: string;
}

export function BrandKitCardPreview({
  kitData,
  previewVideoUrl = null,
  previewPosterUrl = null,
  className,
}: BrandKitCardPreviewProps) {
  const [playing, setPlaying] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loadedFonts, setLoadedFonts] = useState<Set<string>>(new Set());
  const [videoFailed, setVideoFailed] = useState(false);
  // A poster that 404s (e.g. reaped from storage) must not render a broken
  // image: drop it and show the stored video's first frame instead.
  const [failedPosterUrl, setFailedPosterUrl] = useState<string | null>(null);
  const previewRef = useRef<ProjectPreviewRef>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { data: fontsData } = useMyFontsQuery();
  const posterUrl = previewPosterUrl && previewPosterUrl !== failedPosterUrl ? previewPosterUrl : null;
  const useStoredVideo = Boolean(previewVideoUrl) && !videoFailed;
  const showPosterOnly = useStoredVideo && Boolean(posterUrl) && !playing;
  const frameProps = useMemo(
    () => buildBrandKitCardPreviewProps({
      data: kitData,
      mediaOrigin: 'local',
      sampleClipTitle: t('brandKitsNew.sampleClipTitle'),
    }),
    [kitData, t],
  );

  const loadFont = useCallback((fontId: string) => {
    if (!fontsData?.myFonts || loadedFonts.has(fontId)) return;
    const font = fontsData.myFonts.find((candidate) => candidate.id === fontId);
    if (!font?.file_url) return;
    let styleElement = document.getElementById(`font-loader-${fontId}`) as HTMLStyleElement | null;
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = `font-loader-${fontId}`;
      styleElement.textContent = `@font-face { font-family: '${fontId}'; src: url('${font.file_url}') format('truetype'); font-display: swap; }`;
      document.head.appendChild(styleElement);
    }
    setLoadedFonts((prev) => new Set([...prev, fontId]));
  }, [fontsData?.myFonts, loadedFonts]);

  useEffect(() => {
    if (frameProps.subtitleConfig.fontFamily) loadFont(frameProps.subtitleConfig.fontFamily);
  }, [frameProps.subtitleConfig.fontFamily, loadFont]);

  const subtitleConfig = useMemo(
    () => buildSubtitlePreviewConfig({
      subtitleData: frameProps.subtitleConfig,
      resolvedFontFamily: frameProps.subtitleConfig.fontFamily
        && loadedFonts.has(frameProps.subtitleConfig.fontFamily)
        ? frameProps.subtitleConfig.fontFamily
        : null,
    }),
    [frameProps.subtitleConfig, loadedFonts],
  );

  const setPlayback = useCallback((nextPlaying: boolean) => {
    setPlaying(nextPlaying);
    if (nextPlaying) {
      previewRef.current?.play();
      void videoRef.current?.play()?.catch(() => undefined);
    } else {
      previewRef.current?.pause();
      videoRef.current?.pause();
    }
  }, []);

  useEffect(() => {
    if (mounted && playing) {
      previewRef.current?.play();
      void videoRef.current?.play()?.catch(() => undefined);
    }
  }, [mounted, playing]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const hoverRoot = host.parentElement ?? host;
    const onEnter = () => setPlayback(true);
    const onLeave = () => setPlayback(false);
    hoverRoot.addEventListener('mouseenter', onEnter);
    hoverRoot.addEventListener('mouseleave', onLeave);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const visible = shouldPlayVisibleBrandKitPreview(entry);
        setMounted(visible);
        if (!visible) setPlayback(false);
      },
      { threshold: [0, BRAND_KIT_PREVIEW_VISIBLE_RATIO, 1] },
    );
    observer.observe(host);
    return () => {
      observer.disconnect();
      hoverRoot.removeEventListener('mouseenter', onEnter);
      hoverRoot.removeEventListener('mouseleave', onLeave);
    };
  }, [setPlayback]);

  return (
    <div
      ref={hostRef}
      data-testid="brand-kit-card-preview"
      className={cn('relative aspect-[9/16] overflow-hidden bg-black', className)}
      onMouseEnter={() => setPlayback(true)}
      onMouseLeave={() => setPlayback(false)}
    >
      <div className="pointer-events-none h-full w-full">
        {showPosterOnly ? (
          <img
            data-testid="brand-kit-card-preview-poster"
            src={posterUrl ?? undefined}
            alt=""
            className="h-full w-full object-cover"
            onError={() => setFailedPosterUrl(posterUrl)}
          />
        ) : useStoredVideo ? (
          <video
            ref={videoRef}
            data-testid="brand-kit-card-preview-video"
            src={previewVideoUrl ?? undefined}
            poster={posterUrl ?? undefined}
            muted
            loop
            playsInline
            preload={posterUrl ? 'auto' : 'metadata'}
            autoPlay={playing}
            className="h-full w-full object-cover"
            onError={() => setVideoFailed(true)}
          />
        ) : mounted ? (
          <ProjectPreview
            ref={previewRef}
            videoUrl={frameProps.videoUrl}
            startTime={frameProps.startTime}
            endTime={frameProps.endTime}
            width={1080}
            height={1920}
            autoPlay={playing}
            loop
            subtitlesEnabled={frameProps.subtitlesEnabled}
            transcriptionSegments={frameProps.transcriptionSegments}
            isVideoLocked={frameProps.isVideoLocked}
            selectedSubtitlePreset={null}
            logoConfig={frameProps.logoConfig}
            subtitleConfig={subtitleConfig}
            postLayout={frameProps.postLayout}
            showVolumeControls={false}
            showPlayerControls={false}
            volume={0}
            showBufferingPoster={false}
            startMuted
            playback="html5"
            style={{ width: '100%', height: '100%' }}
          />
        ) : null}
      </div>
    </div>
  );
}
